import {pricingMatrixIndexOfReference, acreInSF, pmtFunction} from './constants';

export default {
    name: 'PRICING RANGE',
    description: '',
    hasNamesColumn: true,
    headerRow: [],
    rows: {
        'bps-change': {
            name: 'BPS change',
            hidden: false,
            valueCells: [{
                unit: '%',
                type: 'number',
                editable: true,
                withDecimals: true,
                value: 0.25
            }]
        },
        'price-psf-change': {
            name: 'Price PSF Change',
            hidden: true,
            valueCells: [{
                unit: '$',
                type: 'number',
                editable: true,
                withDecimals: true,
                value: 5
            }]
        },
        'value': {
            name: 'Price',
            generalGetComputedValue(tables, columnIndex) {
                const leaseType = tables['lease-summary'].rows['lease-type'].value;
                const isVacantLeaseType = leaseType === 'Vacant';
                if (isVacantLeaseType) {
                    const grossLeasableArea = tables['offering-summary'].rows['gross-leasable-area'].value;
                    const pricePSF = parseFloat(tables['pricing-matrix'].rows['price-psf'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                    const value = Math.ceil((grossLeasableArea * pricePSF / 5000)) * 5000;
                    const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                    return {internalValue: value, displayValue: displayValue};
                }
                const netOperatingIncome = tables['offering-summary'].rows['net-operating-income'].getComputedValue(tables).internalValue;
                const capRate = parseFloat(tables['pricing-matrix'].rows['cap-rate'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const value = capRate ? Math.ceil((netOperatingIncome / (capRate / 100) / 5000)) * 5000 : null;
                const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['value'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'cap-rate': {
            name: 'Cap Rate',
            hidden: false,
            generalGetComputedValue(tables, bpsMultiplier) {
                const baseCapRate = parseFloat(tables['pricing-matrix'].rows['cap-rate'].valueCells[pricingMatrixIndexOfReference].getComputedValue(tables).internalValue);
                const bpsChange = parseFloat(tables['pricing-matrix'].rows['bps-change'].valueCells[0].value);
                const value = baseCapRate + bpsChange * bpsMultiplier;
                const displayValue = value && !isNaN(value) ? `${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits :2})}%` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                const cell = {
                    unit: '$',
                    type: 'number',
                    editable: false
                };
                if (index === pricingMatrixIndexOfReference) {
                    cell.getComputedValue = function (tables) {
                        const value = parseFloat(tables['offering-summary'].rows['cap-rate'].value);
                        const displayValue = value && !isNaN(value) ? `${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits :2})}%` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                } else {
                    cell.getComputedValue = function (tables) {
                        return tables['pricing-matrix'].rows['cap-rate'].generalGetComputedValue(tables, -1 + index);
                    }
                }
                return cell;
            }),
        },
        'price-psf': {
            name: 'Price PSF',
            hidden: true,
            generalGetComputedValue(tables, pricePsfMultiplier) {
                const basePricePSF = parseFloat(tables['pricing-matrix'].rows['price-psf'].valueCells[pricingMatrixIndexOfReference].getComputedValue(tables).internalValue);
                const pricePsfChange = parseFloat(tables['pricing-matrix'].rows['price-psf-change'].valueCells[0].value);
                const value = parseFloat(basePricePSF + pricePsfChange * pricePsfMultiplier).toFixed(2);
                const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                const cell = {
                    unit: '$',
                    type: 'number',
                    editable: false
                };
                if (index === pricingMatrixIndexOfReference) {
                    cell.getComputedValue = function (tables) {
                        const value = tables['offering-summary'].rows['price-psf'].value;
                        const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                } else {
                    cell.getComputedValue = function (tables) {
                        return tables['pricing-matrix'].rows['price-psf'].generalGetComputedValue(tables, -1 + index);
                    }
                }
                return cell;
            }),
        },
        'cash-on-cash': {
            name: 'Cash on Cash',
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '%',
                    type: 'number',
                    editable: false,
                    tooltip: '= cash_flow / down_payment',
                    getComputedValue(tables) {
                        const cashFlow = tables['pricing-matrix'].rows['cash-flow'].valueCells[index].getComputedValue(tables).internalValue;
                        const requiredEquity = tables['pricing-matrix'].rows['required-equity'].valueCells[index].getComputedValue(tables).internalValue;
                        const value = parseFloat(cashFlow / requiredEquity * 100).toFixed(2);
                        const displayValue  = value && !isNaN(value) ? `${value.toLocaleString()}%` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                }
            }),
        },
        'sale-price-psf': {
            name: 'PSF',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const grossLeasableArea = parseInt(tables['offering-summary'].rows['gross-leasable-area'].value);
                const value = parseFloat(pricingValue / grossLeasableArea).toFixed(2);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price / gross_leasable_area',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['sale-price-psf'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'land-price-psf': {
            name: 'PSF - Land',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const lotSize = parseFloat(tables['offering-summary'].rows['lot-size'].value);
                const value = parseFloat(pricingValue / (lotSize * acreInSF)).toFixed(2);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price / (lot_size * 43560)',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['land-price-psf'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'required-equity': {
            name: 'Down Payment',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const equityPercentage = tables['pricing-matrix-annex'].rows['req-equity'].valueCells[0].value / 100;
                const value = parseInt(pricingValue * equityPercentage);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price * required_equity_percentage',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['required-equity'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'loan-amount': {
            name: 'Loan Amount',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const requiredEquity = parseFloat(tables['pricing-matrix'].rows['required-equity'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const value = parseInt(pricingValue - requiredEquity);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price - down_payment',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['loan-amount'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'loan-payment': {
            name: 'Loan Payment',
            generalGetComputedValue(tables, columnIndex) {
                const interestRate = tables['pricing-matrix-annex'].rows['interest-rate'].valueCells[1].value;
                const amortization = tables['pricing-matrix-annex'].rows['amortization'].valueCells[1].value;
                const loanAmount = tables['pricing-matrix'].rows['loan-amount'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(-pmtFunction(interestRate / 100 / 12, amortization * 12, loanAmount) * 12);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= -PMT(interest_rate/12, amortization*12, loan_amount) * 12',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['loan-payment'].generalGetComputedValue(tables, index);
                    }
                }
            }),

        },
        'cash-flow': {
            name: 'Cash Flow',
            generalGetComputedValue(tables, columnIndex) {
                const netOperatingIncome = tables['offering-summary'].rows['net-operating-income'].getComputedValue(tables).internalValue;
                const loanPayment = tables['pricing-matrix'].rows['loan-payment'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(netOperatingIncome - loanPayment);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= net_operating_income - loan_payment',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['cash-flow'].generalGetComputedValue(tables, index);
                    }
                }
            }),

        },
        'monthly-cash-flow': {
            name: 'Monthly Cash Flow',
            generalGetComputedValue(tables, columnIndex) {
                const loanPayment = tables['pricing-matrix'].rows['cash-flow'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(loanPayment / 12);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= cash_flow / 12',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['monthly-cash-flow'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        }
    },
};